// scrubSecrets — replace common secret-shaped strings with stable redactions
// before any payload leaves the browser. Idempotent (running twice is safe).
//
// Patterns covered:
//   - Google API keys (AIzaSy...) — used by Gemini + Maps
//   - OpenAI keys (sk-... / sk-proj-...)
//   - Groq keys (gsk_...)
//   - Supabase platform tokens (sbp_...)
//   - Supabase API keys (sb_publishable_... / sb_secret_...) — the CURRENT key
//     format. The legacy JWT anon/service_role keys were disabled 2026-06-03,
//     so this is the shape the app actually ships with today; without it the
//     live key would reach system_error_logs verbatim.
//   - JWTs (eyJ...)
//   - Bearer headers (Bearer <token>)
//   - Generic api_key=<value> in URLs/error bodies
//   - Share tokens carried in a query string (?t= / ?token= / ?access_token=).
//     The public-proposal link `/proposal/<id>?t=<32 hex>` IS the credential:
//     whoever holds it opens that client's proposal. Five still-live tokens
//     were found sitting in system_error_logs in plaintext (2026-08-22) — they
//     arrived through `p_route` and through the `window.location.href` copy
//     that logger.ts puts inside `context.browser`.
//
// Used by utils/logger.ts before every report_error RPC, so that an upstream
// API error containing a key (e.g. Gemini 403 with key in the message) never
// lands in system_error_logs verbatim. This is the single biggest secret-leak
// defence in the app — pair with the Edge-Function-side scrub in each AI fn.
const PATTERNS: Array<[RegExp, string]> = [
    [/AIzaSy[A-Za-z0-9_\-]{30,}/g,          'AIzaSy***REDACTED***'],
    [/sk-proj-[A-Za-z0-9_\-]{20,}/g,        'sk-proj-***REDACTED***'],
    [/sk-[A-Za-z0-9_\-]{20,}/g,             'sk-***REDACTED***'],
    [/sbp_[A-Za-z0-9]{20,}/g,               'sbp_***REDACTED***'],
    [/sb_secret_[A-Za-z0-9_\-]{10,}/g,      'sb_secret_***REDACTED***'],
    [/sb_publishable_[A-Za-z0-9_\-]{10,}/g, 'sb_publishable_***REDACTED***'],
    [/Bearer\s+[A-Za-z0-9._\-]{20,}/gi,     'Bearer ***REDACTED***'],
    [/eyJ[A-Za-z0-9._\-]{30,}/g,            'eyJ***REDACTED***'],
    [/gsk_[A-Za-z0-9]{20,}/g,               'gsk_***REDACTED***'],
    [/api_key=[A-Za-z0-9_\-\.]{20,}/g,      'api_key=***REDACTED***'],
    // El separador (`?` o `&`) se captura y se devuelve para no romper la URL:
    // sin `$1` el resultado sería `/proposal/abct=***REDACTED***`. Sigue siendo
    // idempotente: `***REDACTED***` no encaja en `[A-Za-z0-9_\-]{16,}`.
    [/([?&])(t|token|access_token)=[A-Za-z0-9_\-]{16,}/g, '$1$2=***REDACTED***'],
];

/** Scrub a string by replacing every known secret pattern with a stable redaction. */
export function scrubSecrets(s: string | null | undefined): string {
    if (s == null) return '';
    let out = String(s);
    for (const [re, rep] of PATTERNS) out = out.replace(re, rep);
    return out;
}

/** JSON-roundtrip scrub: serialise, scrub the string, parse back. Catches secrets
 *  nested arbitrarily deep in objects without traversing manually. Returns the
 *  original object on parse failure (e.g. circular references). */
export function scrubObject<T>(obj: T): T {
    if (obj == null) return obj;
    try {
        return JSON.parse(scrubSecrets(JSON.stringify(obj))) as T;
    } catch {
        return obj;
    }
}
